import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { toast } from '../components/ui/Toast';
import { api } from '../services/api';
import { useAuthStore } from '../stores/auth';

const loginSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string().min(1, 'Password richiesta'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const response = await api.login(data.email, data.password);
      login(response.user, response.accessToken, response.refreshToken);
      toast({ title: 'Accesso effettuato', variant: 'success' });
      navigate('/');
    } catch (error) {
      toast({
        title: 'Credenziali non valide',
        description: 'Verifica email e password e riprova',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center px-6"
      style={{
        minHeight: 'calc(100dvh - var(--header-height))',
        backgroundColor: 'var(--color-surface-secondary)',
      }}
    >
      <div className="w-full max-w-sm">
        {/* Logo + Title */}
        <div className="text-center mb-8">
          <img
            src="/logo-icon.svg"
            alt="HomeFinder"
            className="mx-auto mb-4 h-14 w-14"
            style={{ borderRadius: 'var(--radius-md)' }}
          />
          <h1
            style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              letterSpacing: 'var(--letter-spacing-tight)',
            }}
          >
            Bentornato
          </h1>
          <p
            className="mt-1"
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            Accedi al tuo account HomeFinder
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label
              className="block mb-1.5"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Email
            </label>
            <Input
              type="email"
              placeholder="mario.rossi@email.it"
              autoComplete="email"
              error={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label
              className="block mb-1.5"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="current-password"
                error={!!errors.password}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-error">{errors.password.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Accesso in corso...' : 'Accedi'}
          </Button>
        </form>

        {/* Footer link */}
        <p
          className="mt-8 text-center"
          style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)' }}
        >
          Non hai un account?{' '}
          <Link
            to="/register"
            style={{ color: 'var(--color-accent)', fontWeight: 'var(--font-weight-semibold)' }}
          >
            Registrati
          </Link>
        </p>
      </div>
    </div>
  );
}
